import { SIDEBAR_COLLAPSED_STORAGE_KEY, SIDEBAR_WIDTH_STORAGE_KEY, SIDEBAR_STATE_EVENT, SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_DEFAULT_WIDTH, clampExpandedWidth, getStoredSidebarWidth, getInitialSidebarCollapsed, getInitialSidebarWidth } from '../utils/dashboardSidebarLayout';
import MobileNotificationsButton from '../components/panels/MobileNotificationsButton';
import React, { useLayoutEffect, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useAuth,
  useClerk,
  useOrganization,
  useUser,
} from '@clerk/clerk-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { checkPermission } from '../utils/auth';
import {
  activateDashboardOrganization,
  getMembershipDisplayName,
  getMembershipOrganizationId,
} from '../utils/activeOrganization';
import { P } from '../utils/permissions';
import { OPEN_ORGANIZATION_PICKER, stripeReviewReturnPath } from '../utils/stripeReviewLink';
import { useDashboardRoutePanelActive } from '../contexts/DashboardRoutePanelContext';
import { getPermissionLabel } from '../components/permissions/PermissionPlaceholder';
import { getMerchantLogoCandidates, getOrganizationInitials } from '../utils/merchantIdentity';
import { useMarketingTierEnabled } from '../hooks/useMarketingTierEnabled';
import { useCompleteOrganizationList } from '../hooks/useCompleteOrganizationList';
import { ChevronRight, Lock, Plus } from 'lucide-react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Cog6ToothIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  BroadcastObjectIcon,
  ContactObjectIcon,
  FormObjectIcon,
  PaymentObjectIcon,
  SegmentObjectIcon,
  SubscriptionObjectIcon,
} from '@/components/icons/DashboardObjectIcons';
import UniversalSearch from '../components/universal-search/UniversalSearch';
import { toast } from 'sonner';
import HomeSetupChecklist from './onboarding/checklist/HomeSetupChecklist';
import CreateDropdown from './dropdowns/CreateDropdown';
import { MOBILE_DESTINATIONS } from '../components/panels/MobileDashboardNavigation';
import { useDashboardAssistantDock } from '../contexts/DashboardAssistantDockContext';

// Add this style block at the top of your file
const styles = `
  #sidebar * {
    -webkit-tap-highlight-color: transparent;
  }
`;

const eventOrgs = [
  'Dev',
  'Thabaat',
  'Muslim Coogs Alumni Network',
  'Nona Muslim Community Association Inc',
  'Muslim Longhorns Alumni Association',
  'Guided Hearts Institute',
  'Nueces Mosque Operations',
  '.NGO Charities',
  'Amaly',
  'Hamd Institute',
  'Texas Muslimahs',
  'Detroit Muslim Running Club',
  'Ihya Foundation',
  'Alpha Lambda Mu Foundation',
  'An Nisa Hope Center',
  'Khalil Islamic Community',
  'Pillars by Hamd',
  'Salaam Foundation',
  'Aghosh AlKhidmat USA',
  'Al Noor Muslim Community Center',
  'Bayan',
  'Guardians Foundation International',
  'Middle Eastern Immigrant and Refugee Alliance (MIRA)',
  'Mihraab Foundation',
  'Rahma Worldwide Aid & Development',
  'The Giving Games Foundation',
  'The Preservers',
  'The SunBundle',
  'Two Point Five Fund',
  'Najam Institute Foundation Inc',
  'Yameen ATX',
  'Iswarah',
  'Kidneys Beyond Borders Inc',
  'Voices of Virtue',
  'Fajr Scientific',
];

// Drag boundary: below this snaps to collapsed, above snaps to expanded.
const SIDEBAR_COLLAPSE_THRESHOLD = 140;
const ROUTER_BASENAME = window.location.pathname.startsWith('/dashboard') ? '/dashboard/' : '/';

const formatPlanName = (value) =>
  String(value)
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getOrganizationPlanLabel = (merchant) => {
  const planValue = merchant?.billingDetails?.plan || merchant?.billingPlan;
  if (!planValue) return '';

  // PE-1636: the legacy planCatalog.ts mirror is retired. formatPlanName is
  // behavior-preserving for every tier the catalog carried (STARTER→"Starter",
  // PLUS→"Plus", …); BASE/PRO/ENTERPRISE were never in the catalog and already
  // fell through to it.
  const planId = String(planValue).toUpperCase();
  return `${formatPlanName(planId)} plan`;
};

function Sidebar({ sidebarOpen, setSidebarOpen, setFormInfo, merchant, selectableForms }) {
  const { assistance: { isCompact, openHelp } } = useDashboardAssistantDock();
  const clerk = useClerk();
  const { orgId } = useAuth();
  const { user: clerkUser } = useUser();
  const { organization } = useOrganization();
  const {
    isLoaded: organizationListLoaded,
    setActive,
    userMemberships,
  } = useCompleteOrganizationList();
  const isClerkLoaded = clerk && clerk.loaded;
  const history = useHistory();
  const [desktopCollapsed, setDesktopCollapsed] = useState(getInitialSidebarCollapsed);
  const [desktopWidth, setDesktopWidth] = useState(getInitialSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [logoCandidateIndex, setLogoCandidateIndex] = useState(0);
  const [organizationMenuOpen, setOrganizationMenuOpen] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState(null);

  const location = useLocation();
  const activePanel = useDashboardRoutePanelActive();
  const { pathname } = location;
  const page = pathname.split('/')[1];

  const sidebar = useRef(null);
  const isDropdownOpen = useRef(false);

  useLayoutEffect(() => {
    const drawer = sidebar.current;
    const content = document.querySelector('[data-dashboard-route-content]');
    if (!drawer) return undefined;
    drawer.inert = isCompact && !sidebarOpen;
    if (content) content.inert = isCompact && sidebarOpen;
    if (isCompact && sidebarOpen) drawer.focus({ preventScroll: true });
    if (isCompact && !sidebarOpen && drawer.contains(document.activeElement)) {
      document.querySelector('[data-mobile-more]')?.focus();
    }
    return () => { drawer.inert = false; if (content) content.inert = false; };
  }, [isCompact, sidebarOpen]);

  useEffect(() => {
    if (!isCompact) setSidebarOpen(false);
  }, [isCompact, setSidebarOpen]);


  const userName = isClerkLoaded
    ? clerkUser?.fullName ||
      [clerkUser?.firstName, clerkUser?.lastName].filter(Boolean).join(' ') ||
      clerkUser?.username ||
      ''
    : '';
  const userEmail = isClerkLoaded
    ? clerkUser?.primaryEmailAddress?.emailAddress ||
      clerkUser?.emailAddresses?.[0]?.emailAddress ||
      ''
    : '';
  const organizationPlanLabel = getOrganizationPlanLabel(merchant);
  const membershipList = userMemberships?.data?.length
    ? userMemberships.data
    : clerkUser?.organizationMemberships || [];
  const activeClerkOrgId =
    orgId || organization?.id || clerk?.session?.lastActiveOrganizationId || '';
  const activeMembership = membershipList.find(
    (membership) => getMembershipOrganizationId(membership) === activeClerkOrgId,
  );
  // Merchant hydration can lag behind Clerk after a new organization is
  // provisioned. Keep the workspace menu available from the authoritative Clerk
  // membership so the user can always create or switch organizations.
  const workspaceName = merchant?.merchantName || getMembershipDisplayName(activeMembership);
  const showOrganizationSwitcher = organizationListLoaded && membershipList.length > 1;

  const handleSwitchOrganization = async (membership) => {
    const clerkOrgId = getMembershipOrganizationId(membership);
    if (!clerkOrgId || clerkOrgId === activeClerkOrgId || switchingOrgId) return;

    const orgName = getMembershipDisplayName(membership);
    setSwitchingOrgId(clerkOrgId);

    try {
      const { syncResult } = await activateDashboardOrganization({
        clerk,
        setActive,
        clerkOrgId,
        syncContext: 'Syncing active organization after dashboard switch',
        redirectUrl: window.location.origin + ROUTER_BASENAME.replace(/\/$/, '') + stripeReviewReturnPath(location),
      });
      setOrganizationMenuOpen(false);

      if (!syncResult?.ok) {
        console.warn('[Sidebar] active-org sync failed', syncResult?.status, syncResult?.error);
        if (syncResult?.status === 404) {
          toast.error('This organization is not connected to CharityStack yet.');
        } else {
          toast.warning('Switched organization, but your last active workspace was not saved.');
        }
        return;
      }

      toast.success(`Switched to ${orgName}`);
    } catch (error) {
      console.error('[Sidebar] organization switch failed:', error);
      toast.error('Could not switch organizations. Please try again.');
    } finally {
      setSwitchingOrgId(null);
    }
  };

  const handleLogout = async () => {
    try {
      const redirectUrl = window.location.origin + ROUTER_BASENAME;
      await clerk.signOut({ redirectUrl });
    } catch (error) {
      console.error('Error signing out:', error);
      window.location.href = window.location.origin + ROUTER_BASENAME;
    }
  };

  const handleCreateOrganization = () => {
    setOrganizationMenuOpen(false);
    history.push('/onboarding?intent=create-organization');
  };

  const updateDesktopSidebar = (collapsed, width, notify = true) => {
    // Invariant: collapsed === true ⇒ width is exactly SIDEBAR_COLLAPSED_WIDTH;
    // collapsed === false ⇒ width is within [SIDEBAR_EXPANDED_MIN_WIDTH, SIDEBAR_MAX_WIDTH].
    // No intermediate widths are permitted, which is what prevents the
    // half-collapsed/clipped-label state.
    const nextWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : clampExpandedWidth(width);
    setDesktopCollapsed(collapsed);
    setDesktopWidth(nextWidth);

    if (notify && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(SIDEBAR_STATE_EVENT, {
          detail: {
            collapsed,
            width: nextWidth,
          },
        }),
      );
    }
  };

  const logoCandidates = useMemo(
    () =>
      getMerchantLogoCandidates(
        merchant,
        organization?.hasImage ? organization.imageUrl : null,
        import.meta.env.VITE_APP_IMAGE_URL,
      ),
    [merchant, organization?.hasImage, organization?.imageUrl],
  );
  const logoCandidatesKey = logoCandidates.join('|');
  const logoUrl = logoCandidates[logoCandidateIndex] || null;

  // Restart the ordered fallback chain when the active organization or its logo
  // metadata changes. Image failures advance through merchant, CDN, and Clerk.
  useEffect(() => {
    setLogoCandidateIndex(0);
  }, [logoCandidatesKey]);

  // Track dropdown open/close state
  useEffect(() => {
    const observer = new MutationObserver(() => {
      // Check if any dropdown menu is currently open
      const openDropdown =
        document.querySelector('[data-radix-popper-content-wrapper]') ||
        document.querySelector('[role="menu"][data-state="open"]');
      isDropdownOpen.current = !!openDropdown;
    });

    // Observe the body for dropdown portal additions/removals
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    });

    return () => observer.disconnect();
  }, []);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = event => {
      if (!sidebarOpen || event.key !== 'Escape' || event.defaultPrevented || isDropdownOpen.current || document.querySelector('[role=dialog]')) return;
      setSidebarOpen(false);
      document.querySelector('[data-mobile-more]')?.focus();
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [sidebarOpen, setSidebarOpen]);

  // On mobile (below the lg breakpoint), close the sidebar whenever the route
  // changes. NavLink items in the sidebar already close it in their onClick,
  // but navigation triggered from elsewhere — CreateDropdown menu items,
  // programmatic history.push, browser back/forward — does not. Without this,
  // the slide-over sidebar stays open and obscures the destination page (e.g.,
  // the guided form tour's /forms/new step where the Fundraiser tile gets
  // hidden under the panel).
  //
  // CRITICAL: sidebarOpen is READ via a ref, not via the dep array. Including
  // sidebarOpen as a dep would cause the effect to re-fire the moment the user
  // OPENS the sidebar (sidebarOpen toggles true → effect re-runs → closes it).
  // That breaks the mobile guided-tour "Open the menu" step. Only `pathname`
  // should trigger this effect — opening the sidebar is not a navigation event.
  const sidebarOpenRef = useRef(sidebarOpen);
  sidebarOpenRef.current = sidebarOpen;
  const isFirstRouteRef = useRef(true);
  useEffect(() => {
    // Skip the very first render — closing the sidebar on initial mount would
    // fight other initialization (e.g., a user landing with sidebar pre-opened
    // for some flow). Only act on subsequent pathname changes.
    if (isFirstRouteRef.current) {
      isFirstRouteRef.current = false;
      return;
    }
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 1024) return; // lg breakpoint — desktop keeps sidebar
    if (!sidebarOpenRef.current) return;
    setSidebarOpen(false);
  }, [pathname, setSidebarOpen]);

  {
    /*
          If the readOnly variable is truthy (evaluates to true), it will return null. In other words, nothing will be rendered in this case.
          If the readOnly variable is falsy (evaluates to false), it will execute the code block enclosed in the parentheses ( and ). This code block can contain JSX elements or other JavaScript code that will be rendered or executed when readOnly is falsy.
          */
  }
  const { readOnly } = useSelector((state) => state.readOnly); // this contains the variable which youll use to check

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, desktopCollapsed ? 'true' : 'false');
      localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(desktopWidth));
    } catch (_) {
      /* storage may be unavailable */
    }
  }, [desktopCollapsed, desktopWidth]);

  useEffect(() => {
    const handleSidebarStateChange = (event) => {
      const collapsed = event.detail?.collapsed;
      const width = Number(event.detail?.width);
      if (typeof collapsed !== 'boolean') return;
      // updateDesktopSidebar enforces the invariant: if collapsed=true the
      // width arg is ignored; if collapsed=false the width is clamped into
      // the expanded range. We still pass a sensible fallback so that
      // event consumers do not need to know the constants.
      const safeWidth = Number.isFinite(width) ? width : getStoredSidebarWidth();
      updateDesktopSidebar(collapsed, safeWidth, false);
    };

    window.addEventListener(SIDEBAR_STATE_EVENT, handleSidebarStateChange);
    return () => window.removeEventListener(SIDEBAR_STATE_EVENT, handleSidebarStateChange);
  }, []);

  useEffect(() => {
    if (!activePanel) return;
    const openPicker = () => {
      setSidebarOpen(true);
      updateDesktopSidebar(false, SIDEBAR_DEFAULT_WIDTH);
      setOrganizationMenuOpen(true);
    };
    window.addEventListener(OPEN_ORGANIZATION_PICKER, openPicker);
    return () => window.removeEventListener(OPEN_ORGANIZATION_PICKER, openPicker);
  }, [activePanel, setSidebarOpen]);

  useEffect(() => {
    if (!isResizing) return undefined;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    // Hard-snap resize: the sidebar is either collapsed (80px) or expanded
    // within [SIDEBAR_EXPANDED_MIN_WIDTH, SIDEBAR_MAX_WIDTH]. Dragging below
    // the threshold collapses; dragging above it expands to at least the
    // expanded minimum. There is no continuous "almost collapsed" zone.
    const handlePointerMove = (event) => {
      if (!sidebar.current) return;
      const { left } = sidebar.current.getBoundingClientRect();
      const pointerWidth = event.clientX - left;
      if (pointerWidth < SIDEBAR_COLLAPSE_THRESHOLD) {
        updateDesktopSidebar(true, SIDEBAR_COLLAPSED_WIDTH);
        return;
      }
      updateDesktopSidebar(false, clampExpandedWidth(pointerWidth));
    };

    const endResize = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    // Listen for pointerup, pointercancel, and visibilitychange so the resize
    // session always ends, even if the user releases outside the window,
    // alt-tabs, or the OS cancels the gesture.
    const handlePointerUp = () => endResize();
    const handlePointerCancel = () => endResize();
    const handleVisibilityChange = () => {
      if (document.hidden) endResize();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isResizing]);

  // PocketFlows Marketing section gate:
  //   marketingTierGate — feature ACCESS (PostHog flag + permission).
  //   Hidden entirely in prod until the PocketFlows launch flag flips.
  //
  // We intentionally do NOT also gate on `marketingActivation === 'active'`
  // anymore — the section is always shown when the feature flag is on.
  // STARTER orgs (and PLUS orgs that paused Marketing) still see the tabs,
  // and the per-page MarketingPaywall renders the upgrade/paused message
  // inside the page chrome. This makes Marketing discoverable without
  // requiring a subscription first.
  const marketingTierGate = useMarketingTierEnabled();
  const menuItems = [
    {
      title: '',
      items: [
        {
          name: 'Home',
          path: '/',
          icon: () => (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-5 w-5"
            >
              <path d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.061l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.689Z" />
              <path d="m12 5.432 8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.432Z" />
            </svg>
          ),
        },
        {
          name: 'Forms',
          path: '/forms',
          permission: P.VIEW_FORMS,
          icon: FormObjectIcon,
          showIf: checkPermission(P.VIEW_FORMS),
          onClick: () => {
            if (setFormInfo) {
              setFormInfo(null);
            }
          },
        },
        {
          name: 'Web Elements',
          path: '/elements',
          permission: P.VIEW_ELEMENTS,
          icon: () => (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-5 w-5"
            >
              <path d="M6 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3H6ZM15.75 3a3 3 0 0 0-3 3v2.25a3 3 0 0 0 3 3H18a3 3 0 0 0 3-3V6a3 3 0 0 0-3-3h-2.25ZM6 12.75a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h2.25a3 3 0 0 0 3-3v-2.25a3 3 0 0 0-3-3H6ZM17.625 13.5a.75.75 0 0 0-1.5 0v2.625H13.5a.75.75 0 0 0 0 1.5h2.625v2.625a.75.75 0 0 0 1.5 0v-2.625h2.625a.75.75 0 0 0 0-1.5h-2.625V13.5Z" />
            </svg>
          ),
          // Shown for discoverability REGARDLESS of entitlement (PE-1624), mirroring
          // the Marketing section below: a STARTER / non-entitled org SEES Elements;
          // clicking lands on the route, whose EntitlementGate renders the Elements
          // engagement / upsell page (ElementsEngagement) rather than the builder.
          // Discovery is open, the real tool stays gated.
          showIf: checkPermission(P.VIEW_ELEMENTS),
        },
        {
          name: 'Settings',
          path: '/settings',
          icon: () => <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />,
        },
      ],
    },
    {
      title: 'Records',
      items: [
        {
          name: 'Contacts',
          path: '/contacts',
          permission: P.VIEW_CONTACTS,
          showIf: checkPermission(P.VIEW_CONTACTS),
          icon: ContactObjectIcon,
        },
        ...(marketingTierGate === 'enabled' ? [
          {
            name: 'Segments',
            path: '/segments',
            permission: P.VIEW_SEGMENTS,
            showIf: checkPermission(P.VIEW_SEGMENTS),
            icon: SegmentObjectIcon,
          },
        ] : []),
      ],
    },
    {
      title: 'Finance',
      items: [
        {
          name: 'Payments',
          path: '/payments',
          permission: P.VIEW_PAYMENTS,
          showIf: checkPermission(P.VIEW_PAYMENTS),
          icon: PaymentObjectIcon,
        },
        {
          name: 'Subscriptions',
          path: '/subscriptions',
          permission: P.VIEW_SUBSCRIPTIONS,
          showIf: checkPermission(P.VIEW_SUBSCRIPTIONS),
          icon: SubscriptionObjectIcon,
        },
        {
          name: 'Payouts',
          path: '/payouts',
          permission: P.VIEW_PAYOUTS,
          showIf: checkPermission(P.VIEW_PAYOUTS),
          icon: () => (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="h-5 w-5"
            >
              <path
                fillRule="evenodd"
                d="M16.5 3.75a1.5 1.5 0 0 1 1.5 1.5v13.5a1.5 1.5 0 0 1-1.5 1.5h-6a1.5 1.5 0 0 1-1.5-1.5V15a.75.75 0 0 0-1.5 0v3.75a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V5.25a3 3 0 0 0-3-3h-6a3 3 0 0 0-3 3V9A.75.75 0 1 0 9 9V5.25a1.5 1.5 0 0 1 1.5-1.5h6Zm-5.03 4.72a.75.75 0 0 0 0 1.06l1.72 1.72H2.25a.75.75 0 0 0 0 1.5h10.94l-1.72 1.72a.75.75 0 1 0 1.06 1.06l3-3a.75.75 0 0 0 0-1.06l-3-3a.75.75 0 0 0-1.06 0Z"
                clipRule="evenodd"
              />
            </svg>
          ),
        },
        /* {
          name: "Funds",
          path: "/funds",
          icon: () => (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
        }, */
      ],
    },
    // Marketing section — shown whenever the PocketFlows rollout gate is on
    // (flag + marketing view permission), REGARDLESS of entitlement (PE-1651).
    // A STARTER / non-entitled org SEES the tabs for discoverability; clicking
    // lands on the marketing engagement / upsell page (the routes' EntitlementGate
    // renders MarketingEngagement), so the real tool never mounts — discovery is
    // open, data stays gated. This matches the intent comment near
    // `marketingTierGate` above; the old "never sees the section" behavior (an
    // accidental side effect of the PE-1624 entitlement sweep) is intentionally
    // reverted here.
    ...(marketingTierGate === 'enabled'
      ? [
          {
            title: 'Marketing',
            items: [
              {
                name: 'Broadcasts',
                path: '/broadcasts',
                permission: P.VIEW_BROADCASTS,
                showIf: checkPermission(P.VIEW_BROADCASTS),
                icon: BroadcastObjectIcon,
              },
              {
                name: 'Brand Kit',
                path: '/brand-kit',
                // Branding is a marketing-settings capability — gate on
                // MANAGE_MARKETING (matches the page's own permission check).
                // Relocated here from Settings → Marketing (PE-1670).
                permission: P.MANAGE_MARKETING,
                showIf: checkPermission(P.MANAGE_MARKETING),
                icon: () => (
                  // Swatch (heroicons solid) — implies brand palette / colors.
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-5 w-5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.25 4.125c0-1.036.84-1.875 1.875-1.875h5.25c1.036 0 1.875.84 1.875 1.875V17.25a4.5 4.5 0 1 1-9 0V4.125Zm4.5 14.25a1.125 1.125 0 1 0 0-2.25 1.125 1.125 0 0 0 0 2.25Z"
                      clipRule="evenodd"
                    />
                    <path d="M10.719 21.75h9.156c1.036 0 1.875-.84 1.875-1.875v-5.25c0-1.036-.84-1.875-1.875-1.875h-.14l-8.742 8.743c-.09.089-.18.175-.274.257ZM12.738 17.625l6.474-6.474a1.875 1.875 0 0 0 0-2.651L15.5 4.787a1.875 1.875 0 0 0-2.651 0l-.1.099V17.25c0 .126-.003.251-.01.375Z" />
                  </svg>
                ),
              },
              // Email Blasts + Email Templates nav items removed (PE-1671).
            ],
          },
        ]
      : []),
    // {
    //   title: "Connect",
    //   // showIf: groups.some((group) => eventOrgs.includes(group)),
    //   showIf: true,
    //   items: [
    //     {
    //       name: "Events",
    //       path: "/events",
    //       icon: () => (
    //         <svg
    //           xmlns="http://www.w3.org/2000/svg"
    //           viewBox="0 0 24 24"
    //           fill="currentColor"
    //           className="h-5 w-5"
    //         >
    //           <path d="M12.75 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM7.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM8.25 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM9.75 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM10.5 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM12.75 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM14.25 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" />
    //           <path
    //             fill-rule="evenodd"
    //             d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z"
    //             clip-rule="evenodd"
    //           />
    //         </svg>
    //       ),
    //       showIf: !readOnly,
    //     },
    //     {
    //       name: "Tickets",
    //       path: "/tickets",
    //       icon: () => (
    //         <svg
    //           xmlns="http://www.w3.org/2000/svg"
    //           viewBox="0 0 24 24"
    //           fill="currentColor"
    //           className="h-5 w-5"
    //         >
    //           <path
    //             fill-rule="evenodd"
    //             d="M1.5 6.375c0-1.036.84-1.875 1.875-1.875h17.25c1.035 0 1.875.84 1.875 1.875v3.026a.75.75 0 0 1-.375.65 2.249 2.249 0 0 0 0 3.898.75.75 0 0 1 .375.65v3.026c0 1.035-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 0 1 1.5 17.625v-3.026a.75.75 0 0 1 .374-.65 2.249 2.249 0 0 0 0-3.898.75.75 0 0 1-.374-.65V6.375Zm15-1.125a.75.75 0 0 1 .75.75v.75a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75Zm.75 4.5a.75.75 0 0 0-1.5 0v.75a.75.75 0 0 0 1.5 0v-.75Zm-.75 3a.75.75 0 0 1 .75.75v.75a.75.75 0 0 1-1.5 0v-.75a.75.75 0 0 1 .75-.75Zm.75 4.5a.75.75 0 0 0-1.5 0V18a.75.75 0 0 0 1.5 0v-.75ZM6 12a.75.75 0 0 1 .75-.75H12a.75.75 0 0 1 0 1.5H6.75A.75.75 0 0 1 6 12Zm.75 2.25a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3Z"
    //             clip-rule="evenodd"
    //           />
    //         </svg>
    //       ),
    //     },
    //   ],
    // },
  ];

  // More uses the same destinations and permission decisions as the desktop sidebar.
  const moreItems = menuItems.filter(section => section.showIf !== false)
    .flatMap(section => section.items)
    .filter(item => !MOBILE_DESTINATIONS.some(destination => destination.path === item.path));
  const menuSections = isCompact
    ? [{ title: '', items: [
      ...moreItems.filter(item => item.path !== '/settings'),
      ...moreItems.filter(item => item.path === '/settings'),
      {
        name: 'Help Center',
        path: '#',
        icon: () => <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden="true" />,
        onClick: openHelp,
      },
    ] }]
    : menuItems;

  const renderMenuItem = (item) => {
    const isPermissionLocked = item.showIf === false;
    const lockedDescription = item.permission
      ? `Requires ${getPermissionLabel(item.permission)}. Ask an organization admin to update your access.`
      : 'Ask an organization admin to update your access.';

    // Check if this is a button-type item (no real navigation path)
    const isButton = item.path === '#';
    // Match the active item by comparing pathname against item.path
    // (exact match, or path-with-trailing-slash for sub-routes). Using
    // item.path instead of `item.name.toLowerCase() === pathname.split('/')[1]`
    // because the old logic only compared the FIRST URL segment, which
    // broke for any item whose name doesn't equal its first path segment
    // (historically Broadcasts at the old nested `/marketing/broadcasts`
    // path — segment 1 was "marketing", item name "Broadcasts"; the
    // marketing routes are top-level since 2026-06-12 but path-based
    // matching stays the robust choice).
    const isActive =
      !isButton &&
      (pathname === item.path || (item.path !== '/' && pathname.startsWith(`${item.path}/`)));
    const itemContent = (
      <div className={`flex w-full items-center ${isCompact ? 'min-h-11' : ''} ${desktopCollapsed ? 'lg:justify-center' : ''}`}>
        <item.icon />
        <span className={`ml-2 text-base font-medium ${desktopCollapsed ? 'lg:hidden' : ''}`}>
          {item.name}
        </span>
        {isCompact && !isPermissionLocked && <ChevronRight className="ml-auto h-4 w-4 text-ink-muted" aria-hidden="true" />}
        {isPermissionLocked && (!desktopCollapsed || isCompact) && (
          <Lock className="ml-auto h-3.5 w-3.5 shrink-0 text-ink-muted" aria-hidden="true" />
        )}
        {item.badge && !desktopCollapsed && (
          <span className="ml-auto rounded-full border border-sage/30 bg-sage-light px-2 py-0.5 text-[10px] font-semibold text-[#4A6B4A]">
            {item.badge}
          </span>
        )}
      </div>
    );

    const triggerContent = isPermissionLocked ? (
      <button
        type="button"
        className="block w-full cursor-not-allowed text-left text-ink-muted outline-none transition duration-150 focus:outline-none lg:text-center"
        onClick={(event) => event.preventDefault()}
        aria-label={`${item.name} unavailable`}
        aria-disabled="true"
        title={lockedDescription}
      >
        {itemContent}
      </button>
    ) : isButton ? (
      <button
        className="block w-full text-left text-ink-secondary outline-none transition duration-150 hover:text-ink focus:outline-none lg:text-center"
        onClick={item.onClick ?? (() => {})}
        aria-label={item.name}
      >
        {itemContent}
      </button>
    ) : (
      <NavLink
        exact
        to={item.path}
        aria-label={item.name}
        className={`block w-full outline-none transition duration-150 focus:outline-none ${
          isActive ? 'text-ink' : 'text-ink-secondary hover:text-ink'
        }`}
        onClick={() => {
          // Close sidebar on mobile when navigating
          if (window.innerWidth < 1024) {
            // lg breakpoint
            setSidebarOpen(false);
          }
          // Call any custom onClick handler if provided
          if (item.onClick) {
            item.onClick();
          }
        }}
      >
        {itemContent}
      </NavLink>
    );

    return (
      <li
        key={item.name}
        data-sidebar-item={item.name.toLowerCase()}
        data-permission-locked={isPermissionLocked ? 'true' : undefined}
        data-required-permission={isPermissionLocked ? item.permission : undefined}
        className={`w-full pt-1 outline-none last:mb-0 focus:outline-none ${
          desktopCollapsed ? 'lg:flex lg:justify-center lg:pr-0' : ''
        }`}
      >
        <div
          className={`w-full rounded-md transition duration-150 ${
            desktopCollapsed ? 'lg:h-8 lg:w-8' : ''
          } ${
            isPermissionLocked
              ? 'text-ink-muted opacity-70'
              : isActive
                ? 'bg-paper-inset'
                : 'text-ink-secondary hover:bg-paper-inset'
          }`}
        >
          <div
            className={`w-full ${
              desktopCollapsed
                ? 'px-3 py-1 lg:flex lg:h-full lg:items-center lg:justify-center lg:p-0'
                : 'px-3 py-0.5 pr-3'
            }`}
          >
            {desktopCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>{triggerContent}</TooltipTrigger>
                <TooltipContent side="right" className="hidden lg:block">
                  {isPermissionLocked ? lockedDescription : item.name}
                </TooltipContent>
              </Tooltip>
            ) : (
              triggerContent
            )}
          </div>
        </div>
      </li>
    );
  };

  const renderMenuSection = (section) => {
    if (section.showIf === false) return null;
    const hasVisibleItems = section.items.length > 0;
    if (!hasVisibleItems) return null;
    return (
      <div
        key={section.title || 'workspace'}
        data-sidebar-section={section.title.toLowerCase() || 'workspace'}
        className={section.title ? 'pt-2' : undefined}
      >
        {section.title && <p className={`text-sm text-ink-muted ${desktopCollapsed ? 'lg:sr-only' : ''}`}>
          {section.title}
        </p>}
        <ul className={isCompact ? "mt-0 rounded-xl border border-edge bg-paper-light p-2" : "mt-0"}>{section.items.map(renderMenuItem)}</ul>
      </div>
    );
  };

  return (
    <div
      className="flex min-h-0 flex-col transition-all duration-200 lg:w-[var(--dashboard-sidebar-width)]"
      style={{ '--dashboard-sidebar-width': `${desktopWidth}px` }}
    >
      {/* Add this style tag to apply the CSS rule */}
      <style>{styles}</style>

      {/* Sidebar */}
      <div
        id="sidebar"
        ref={sidebar}
        tabIndex={-1}
        aria-label="Dashboard menu"
        className={`no-scrollbar absolute inset-x-0 top-0 z-40 h-[calc(100dvh-var(--dashboard-shell-bottom-bar-height,0px))] w-full flex-shrink-0 flex-col overflow-hidden bg-paper-muted px-4 outline-none lg:relative lg:left-auto lg:right-auto lg:top-auto lg:h-full lg:w-[var(--dashboard-sidebar-width)] ${sidebarOpen ? 'flex' : 'hidden lg:flex'}`}
      >
        <TooltipProvider delayDuration={150}>
          <div className="flex min-h-0 flex-1 flex-col md:mx-auto md:w-full md:max-w-[640px] lg:max-w-none">
          {/* Organization Logo + Name */}
          <div
            className={`mb-3 mt-1.5 flex items-center gap-2 ${
              desktopCollapsed ? 'lg:justify-center' : ''
            }`}
          >
            {workspaceName ? (
              <DropdownMenu open={organizationMenuOpen} onOpenChange={setOrganizationMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={`group flex h-8 min-w-0 flex-1 items-center gap-2 rounded-lg pr-2 text-left transition duration-150 hover:bg-paper-inset focus:outline-none focus:ring-0 focus-visible:ring-0 ${
                      desktopCollapsed ? 'lg:w-8 lg:flex-none lg:justify-center lg:pr-0' : ''
                    }`}
                    aria-label={`Open ${workspaceName} organization menu`}
                  >
                    <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg border border-edge-input bg-white">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={workspaceName}
                          className="h-full w-full object-contain p-0.5"
                          onError={() => setLogoCandidateIndex((index) => index + 1)}
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-ink-secondary">
                          {getOrganizationInitials(workspaceName).slice(0, 1)}
                        </div>
                      )}
                    </div>
                    <span
                      className={`min-w-0 flex-1 truncate text-base font-normal text-ink ${
                        desktopCollapsed ? 'lg:hidden' : ''
                      }`}
                    >
                      {workspaceName}
                    </span>
                    <ChevronDownIcon
                      className={`h-4 w-4 flex-shrink-0 text-ink-muted opacity-100 lg:opacity-0 transition duration-150 group-hover:opacity-100 group-focus-visible:opacity-100 group-data-[state=open]:opacity-100 ${
                        organizationMenuOpen ? 'rotate-180 opacity-100' : ''
                      } ${desktopCollapsed ? 'lg:hidden' : ''}`}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="flex max-h-[min(36rem,var(--radix-dropdown-menu-content-available-height))] w-[min(640px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl p-1.5 shadow-lg lg:max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] lg:w-72"
                  align="start"
                  side="bottom"
                  sideOffset={8}
                >
                  <div className="flex-shrink-0 px-2.5 pb-2 pt-2">
                    <div className="min-w-0">
                      <p className="truncate text-[15px] font-normal leading-5 text-ink">
                        {workspaceName}
                      </p>
                      {organizationPlanLabel && (
                        <p className="mt-0.5 truncate text-xs leading-4 text-ink-muted">
                          {organizationPlanLabel}
                        </p>
                      )}
                    </div>
                  </div>

                  {showOrganizationSwitcher && (
                    <>
                      <DropdownMenuSeparator className="mx-2 my-1.5 flex-shrink-0 bg-edge" />
                      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1 py-1">
                        {membershipList.map((membership) => {
                          const clerkOrgId = getMembershipOrganizationId(membership);
                          const orgName = getMembershipDisplayName(membership);
                          const isCurrent = clerkOrgId === activeClerkOrgId;
                          const isSwitching = switchingOrgId === clerkOrgId;

                          return (
                            <DropdownMenuItem
                              key={clerkOrgId || orgName}
                              disabled={isCurrent || !!switchingOrgId}
                              aria-current={isCurrent ? 'true' : undefined}
                              className="flex min-h-10 cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-sm text-ink hover:bg-paper-inset focus:bg-paper-inset disabled:cursor-default disabled:opacity-100"
                              onSelect={(event) => {
                                event.preventDefault();
                                handleSwitchOrganization(membership);
                              }}
                            >
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-medium text-ink">{orgName}</p>
                                {isCurrent && (
                                  <p className="truncate text-xs text-ink-muted">
                                    Current workspace
                                  </p>
                                )}
                              </div>
                              {isSwitching && (
                                <span
                                  className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-edge-input border-t-ink-muted"
                                  aria-hidden="true"
                                />
                              )}
                            </DropdownMenuItem>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <DropdownMenuSeparator className="mx-2 my-1.5 flex-shrink-0 bg-edge" />
                  <DropdownMenuItem
                    className="min-h-10 flex-shrink-0 cursor-pointer rounded-md px-2.5 py-2 text-sm font-medium text-ink hover:bg-paper-inset focus:bg-paper-inset"
                    onSelect={(event) => {
                      event.preventDefault();
                      handleCreateOrganization();
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4 text-ink-muted" aria-hidden="true" />
                    Create organization
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="mx-2 my-1.5 flex-shrink-0 bg-edge" />
                  {(userName || userEmail) && (
                    <div className="flex-shrink-0 px-2.5 py-2">
                      {userName && (
                        <p className="truncate text-sm font-medium text-ink">{userName}</p>
                      )}
                      {userEmail && <p className="truncate text-xs text-ink-muted">{userEmail}</p>}
                    </div>
                  )}
                  <DropdownMenuItem
                    className="min-h-9 flex-shrink-0 cursor-pointer rounded-md px-2.5 py-2 text-sm font-medium text-ink-secondary hover:bg-paper-inset focus:bg-paper-inset"
                    onClick={handleLogout}
                  >
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {isCompact && sidebarOpen && <MobileNotificationsButton />}
          </div>

          {merchant?.merchantName && (
            <div className={`mb-3 flex items-center gap-1.5 ${desktopCollapsed ? 'lg:flex-col' : ''}`}>
              <div className={`min-w-0 flex-1 ${desktopCollapsed ? 'lg:w-8 lg:flex-none' : ''}`}>
                <CreateDropdown
                  merchant={merchant}
                  selectableForms={selectableForms}
                  showLabel
                  compactOnDesktop={desktopCollapsed}
                />
              </div>
              <div className="w-11 shrink-0 lg:w-8">
                <UniversalSearch
                  merchant={merchant}
                  compact
                  placeholder="Search for anything"
                  showShortcut={false}
                  showShortcutTooltip
                  collapseToIconBelowLg
                  collapseToIconAtLg
                  tooltipSide={desktopCollapsed ? 'right' : 'bottom'}
                />
              </div>
            </div>
          )}

          <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain px-2 -mx-2 lg:overflow-visible">
          <div className="shrink-0 pb-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">{menuSections.map(renderMenuSection)}</div>
          <HomeSetupChecklist key={merchant?.organizationId || merchant?.merchantName} merchant={merchant} selectableForms={selectableForms} sidebarCollapsed={desktopCollapsed} onExpandSidebar={() => updateDesktopSidebar(false, SIDEBAR_DEFAULT_WIDTH)} onNavigate={() => setSidebarOpen(false)} />
          </div>
          </div>
        </TooltipProvider>
        <button
          type="button"
          className="absolute right-0 top-0 hidden h-full w-2 cursor-col-resize touch-none border-r border-transparent transition-colors duration-150 hover:border-edge-input hover:bg-paper-inset/60 lg:block"
          onPointerDown={(event) => {
            event.preventDefault();
            setIsResizing(true);
          }}
          aria-label="Resize sidebar"
        />
      </div>
    </div>
  );
}

// Define icon components here (HomeIcon, TransactionsIcon, etc.)
// These should be the same SVG content you're currently using

export default Sidebar;
